import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import { SubpageNav } from '../components/SubpageNav';
import { SubpageCTA } from '../components/SubpageCTA';
import { fadeUp } from '../utils/animations';
import { stuckItems, needsItems, consequences, balancePairs, roles, workPrinciples, ctaPersonas } from '../data/omrade1-data';
export function Omrade1() {
  return <div className="min-h-screen bg-background text-ink font-sans" data-id="element-385">
      <SubpageNav activePage="omrade-1" data-id="element-386" />

      <main className="pt-20" data-id="element-387">
        {/* ── HERO ── */}
        <section className="relative py-20 md:py-28 px-6 bg-background overflow-hidden" data-id="element-388">
          {/* Background graphic — abstract compass/direction motif */}
          <motion.div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none select-none" initial={{
          opacity: 0,
          scale: 0.92
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1.8,
          ease: 'easeOut',
          delay: 0.1
        }} data-id="element-389">
            <svg width="560" height="560" viewBox="0 0 560 560" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-[0.13]" data-id="element-390">
              <circle cx="280" cy="280" r="260" stroke="#C4714A" strokeWidth="1" fill="none" data-id="element-391" />
              <circle cx="280" cy="280" r="190" stroke="#C4714A" strokeWidth="1" fill="none" data-id="element-392" />
              <circle cx="280" cy="280" r="120" stroke="#C4714A" strokeWidth="0.75" fill="none" data-id="element-393" />
              <circle cx="280" cy="280" r="4" fill="#C4714A" data-id="element-394" />
              <line x1="280" y1="20" x2="280" y2="540" stroke="#C4714A" strokeWidth="0.75" data-id="element-395" />
              <line x1="20" y1="280" x2="540" y2="280" stroke="#C4714A" strokeWidth="0.75" data-id="element-396" />
              <line x1="96" y1="96" x2="464" y2="464" stroke="#C4714A" strokeWidth="0.5" data-id="element-397" />
              <line x1="464" y1="96" x2="96" y2="464" stroke="#C4714A" strokeWidth="0.5" data-id="element-398" />
              {Array.from({
              length: 12
            }).map((_, i) => {
              const angle = i * 30 * Math.PI / 180;
              const x1 = 280 + 250 * Math.cos(angle);
              const y1 = 280 + 250 * Math.sin(angle);
              const x2 = 280 + 265 * Math.cos(angle);
              const y2 = 280 + 265 * Math.sin(angle);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C4714A" strokeWidth="1.5" data-id="element-399" />;
            })}
              <line x1="280" y1="280" x2="420" y2="140" stroke="#C4714A" strokeWidth="1.5" data-id="element-400" />
              <polygon points="420,140 398,148 412,162" fill="#C4714A" data-id="element-401" />
            </svg>
          </motion.div>

          <div className="max-w-4xl mx-auto relative z-10" data-id="element-402">
            <motion.span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-4 block" custom={0} initial="hidden" animate="visible" variants={fadeUp} data-id="element-403">
              Område 01
            </motion.span>
            <motion.h1 className="text-4xl md:text-6xl font-light text-ink leading-[1.1] mb-6" custom={1} initial="hidden" animate="visible" variants={fadeUp} data-id="element-404">
              Du leder bolaget –{' '}
              <span className="text-terracotta" data-id="element-405">
                men hinner inte leda utvecklingen
              </span>
            </motion.h1>
            <motion.p className="text-xl text-muted font-light max-w-2xl leading-relaxed mb-8" custom={2} initial="hidden" animate="visible" variants={fadeUp} data-id="element-406">
              Låter bekant? Bra. Då är du på rätt ställe.
            </motion.p>
            <motion.div className="w-16 h-1 bg-terracotta rounded-full" custom={3} initial="hidden" animate="visible" variants={fadeUp} data-id="element-407" />
          </div>
        </section>

        {/* ── PROBLEM ── */}
        <section className="py-16 md:py-24 px-6 bg-card/40" data-id="element-408">
          <div className="max-w-4xl mx-auto" data-id="element-409">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-410">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-411">
                01 — Problemet
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-6" data-id="element-412">
                Verksamheten rullar. Kunderna är nöjda.
                <br data-id="element-413" />
                <span className="text-muted" data-id="element-414">
                  Men utvecklingen… den går långsammare än du vill.
                </span>
              </h2>
              <p className="text-lg text-muted leading-relaxed max-w-2xl" data-id="element-415">
                Du är VD, ägare eller chef i ett bolag med 10–50 medarbetare. Du
                vet vad som behöver göras. Du hinner bara inte göra det.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-416">
              <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-background rounded-2xl p-8 border border-border/50" data-id="element-417">
                <h3 className="text-sm font-medium tracking-widest uppercase text-terracotta mb-6" data-id="element-418">
                  Du fastnar i
                </h3>
                <ul className="space-y-3" data-id="element-419">
                  {stuckItems.map((item, i) => <li key={i} className="flex items-start gap-3 text-muted" data-id="element-420">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" data-id="element-421" />
                      <span className="text-sm leading-relaxed" data-id="element-422">{item}</span>
                    </li>)}
                </ul>
              </motion.div>

              <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-background rounded-2xl p-8 border border-border/50" data-id="element-423">
                <h3 className="text-sm font-medium tracking-widest uppercase text-sage mb-6" data-id="element-424">
                  Du vet att bolaget behöver
                </h3>
                <ul className="space-y-3" data-id="element-425">
                  {needsItems.map((item, i) => <li key={i} className="flex items-start gap-3 text-muted" data-id="element-426">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage mt-2 flex-shrink-0" data-id="element-427" />
                      <span className="text-sm leading-relaxed" data-id="element-428">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 bg-ink rounded-2xl px-8 py-7" data-id="element-429">
              <p className="text-white/90 text-lg leading-relaxed" data-id="element-430">
                Vad händer om ingenting förändras?{' '}
                <span className="text-white font-medium" data-id="element-431">
                  Initiativ tappar fart. Möten ger inte förflyttning.
                </span>{' '}
                <span className="text-white/60" data-id="element-432">
                  Och om ett år sitter du i exakt samma situation — fast lite
                  tröttare.
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── KONSEKVENSER ── */}
        <section className="py-16 md:py-24 px-6 bg-background" data-id="element-433">
          <div className="max-w-4xl mx-auto" data-id="element-434">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-435">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-436">
                02 — Konsekvenser
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-437">
                När utvecklingskraften uteblir
              </h2>
              <p className="text-muted text-lg" data-id="element-438">
                Det händer inte dramatiskt. Det smyger sig på.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-439">
              {consequences.map((item, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-card rounded-2xl p-6 border border-border/50" data-id="element-440">
                  <item.icon className="w-5 h-5 text-terracotta mb-4" strokeWidth={1.5} data-id="element-441" />
                  <p className="text-ink font-medium text-sm mb-1" data-id="element-442">
                    {item.text}
                  </p>
                  <p className="text-muted text-sm" data-id="element-443">{item.sub}</p>
                </motion.div>)}
            </div>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 text-center" data-id="element-444">
              <p className="text-xl text-ink font-light" data-id="element-445">
                Det kostar tempo, engagemang och lönsamhet.
              </p>
              <p className="text-muted mt-2" data-id="element-446">
                Och framför allt: mental energi.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── TROVÄRDIGHET ── */}
        <section className="py-16 md:py-24 px-6 bg-ink text-white" data-id="element-447">
          <div className="max-w-4xl mx-auto" data-id="element-448">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-449">
              <span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-450">
                Perspektiv
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-4" data-id="element-451">
                Jag känner igen mönstren.
                <br data-id="element-452" />
                <span className="text-white/60" data-id="element-453">
                  Men din lösning är inte någon annans.
                </span>
              </h2>
              <p className="text-white/70 text-lg max-w-2xl leading-relaxed" data-id="element-454">
                Jag har varit VD. Jag vet hur det känns att balansera det som
                aldrig riktigt går att balansera. Men det betyder inte att jag
                kommer med färdiga svar — varje bolag har sin egen dynamik. Vi
                utgår alltid från din verklighet, inte från gamla best
                practices.
              </p>
            </motion.div>

            <p className="text-white/40 text-xs font-medium tracking-widest uppercase mb-4" data-id="element-455">
              Återkommande motkrafter
            </p>

            <div className="space-y-3" data-id="element-456">
              {balancePairs.map((pair, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="flex items-center gap-4 bg-white/5 rounded-xl px-6 py-4 border border-white/10" data-id="element-457">
                  <span className="text-white font-medium text-sm flex-1 text-right" data-id="element-458">
                    {pair.left}
                  </span>
                  <span className="text-terracotta text-xs font-medium tracking-widest uppercase px-3" data-id="element-459">
                    vs
                  </span>
                  <span className="text-white/70 text-sm flex-1" data-id="element-460">
                    {pair.right}
                  </span>
                </motion.div>)}
            </div>

            <motion.p custom={6} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 text-white/50 text-sm italic" data-id="element-461">
              Balansen är inte att försöka göra allt, men att medvetet våga
              välja.
            </motion.p>
          </div>
        </section>

        {/* ── LÖSNING ── */}
        <section className="py-16 md:py-24 px-6 bg-background" data-id="element-462">
          <div className="max-w-4xl mx-auto" data-id="element-463">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-464">
              <span className="text-sage font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-465">
                03 — Lösningen
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-466">
                En dag i veckan.{' '}
                <span className="text-terracotta" data-id="element-467">
                  Som skapar riktning och rörelse.
                </span>
              </h2>
              <p className="text-muted text-lg max-w-2xl leading-relaxed" data-id="element-468">
                Jag kliver in operativt — inte som en konsult som levererar
                rapporter, utan som en person som faktiskt hjälper er komma
                framåt.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-14" data-id="element-469">
              {roles.map((role, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className={`flex items-start gap-4 rounded-xl p-5 border ${role.humor ? 'bg-sage/8 border-sage/25 border-dashed' : 'bg-card border-border/50'}`} data-id="element-470">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${role.humor ? 'bg-sage/15' : 'bg-terracotta/10'}`} data-id="element-471">
                    <role.icon className={`w-4 h-4 ${role.humor ? 'text-sage' : 'text-terracotta'}`} strokeWidth={1.5} data-id="element-472" />
                  </div>
                  <span className={`text-sm leading-relaxed ${role.humor ? 'text-sage-dark italic' : 'text-ink'}`} data-id="element-473">
                    {role.label}
                  </span>
                </motion.div>)}
            </div>

            <motion.div custom={6} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-card/60 rounded-2xl p-8 border border-border/50" data-id="element-474">
              <h3 className="text-sm font-medium tracking-widest uppercase text-ink mb-6" data-id="element-475">
                Hur vi arbetar
              </h3>
              <ul className="space-y-4" data-id="element-476">
                {workPrinciples.map((p, i) => <li key={i} className="flex items-start gap-3" data-id="element-477">
                    <CheckCircle2 className="w-4 h-4 text-sage mt-0.5 flex-shrink-0" strokeWidth={1.5} data-id="element-478" />
                    <span className="text-muted text-sm leading-relaxed" data-id="element-479">
                      {p}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-8 text-ink font-medium text-sm border-t border-border/50 pt-6" data-id="element-480">
                Målet är inte fler dokument.{' '}
                <span className="text-terracotta" data-id="element-481">
                  Målet är ökad förmåga i organisationen.
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── CTA ── */}
        <SubpageCTA badgeText="1 dag / vecka" badgeColor="terracotta" heading="Känner du igen dig?" subheading="Oavsett vilken roll du har — utmaningen är ofta densamma. Du vet vad som behöver göras. Du hinner bara inte." personas={ctaPersonas} ctaText="Boka ett samtal – helt utan förpliktelser." ctaSubtext="Vi pratar om din situation, inte om mina PowerPoints." backLinkText="Tillbaka till alla områden" backLinkUrl="/" data-id="element-482" />
      </main>
      <Footer data-id="element-483" />
    </div>;
}